import { API } from "../../../services";
import { getApiErrorMessage, handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";

const basePath = `${API_URL.idp}/permission`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).toLowerCase() } : {}),
    ...extra,
  },
});

const getAllTenantPermissions = async (tenantId) => {
  return API.get(
    basePath,
    "tenant_permissions_list",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantPermissionByName = async (permissionName, tenantId) => {
  if (!permissionName) {
    throw new Error("Permission name is missing.");
  }

  return API.get(
    `${basePath}/${encodeURIComponent(permissionName)}`,
    "tenant_permission_by_name",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const putTenantPermission = (permissionKey, payload = {}, tenantId) =>
  API.put(
    `${basePath}/${encodeURIComponent(permissionKey)}`,
    "tenant_permission_update",
    {
      name: String(payload.name || "").trim(),
      allow: Array.isArray(payload.allow)
        ? payload.allow.filter(Boolean).map(String)
        : [],
      deny: Array.isArray(payload.deny)
        ? payload.deny.filter(Boolean).map(String)
        : [],
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);

const createTenantPermission = async (payload = {}, tenantId) => {
  const name = String(payload.name || "").trim();
  if (!name) {
    throw new Error("Permission name is required.");
  }

  try {
    return await API.post(
      basePath,
      "tenant_permission_create",
      { name },
      tenantHeaders(tenantId)
    ).then(handleResponse);
  } catch (err) {
    const message = getApiErrorMessage(err);
    // Previous attempts may have created the permission even when UI showed an error.
    if (/already exists/i.test(message)) {
      try {
        const existing = await getTenantPermissionByName(name, tenantId);
        if (existing) {
          return { ...existing, name, _alreadyExisted: true };
        }
      } catch {
        // fall through
      }
      return { name, _alreadyExisted: true };
    }
    throw err;
  }
};

const isPermissionMissing = (err) =>
  /does not exist/i.test(getApiErrorMessage(err));

const updateTenantPermission = async (permission, payload = {}, tenantId) => {
  const originalName = String(
    typeof permission === "string" ? permission : permission?.name || ""
  ).trim();
  const originalId = String(
    typeof permission === "object"
      ? permission?.permissionId || permission?.id || ""
      : ""
  ).trim();
  const keys = [originalName, originalId].filter(
    (key, index, list) => key && list.indexOf(key) === index
  );

  if (!keys.length) {
    throw new Error("Permission name is missing.");
  }

  let lastError;
  for (let index = 0; index < keys.length; index += 1) {
    try {
      return await putTenantPermission(keys[index], payload, tenantId);
    } catch (err) {
      lastError = err;
      if (!isPermissionMissing(err) || index === keys.length - 1) {
        throw err;
      }
    }
  }

  throw lastError;
};

const deleteTenantPermission = async (permissionName, tenantId) => {
  if (!permissionName) {
    throw new Error("Permission name is missing.");
  }

  return API.delete(
    `${basePath}/${encodeURIComponent(permissionName)}`,
    "tenant_permission_delete",
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

export const tenantPermissionService = {
  getAllTenantPermissions,
  getTenantPermissionByName,
  createTenantPermission,
  updateTenantPermission,
  deleteTenantPermission,
};
